import type {
  RequestTrackingData,
  RequestTrackingItem,
} from '../../../api/cmail.api';

/**
 * Readable labels for a Request Tracking row.
 *
 * The item id is a correlation id (a UUID, or `dropped:<uuid>` / `escalated:<uuid>` for an item
 * tracked without being sent) and means nothing to the person reading the table. Invoice items
 * carry invoiceNumber, vendorName and amount in their metadata; those are what identify the row.
 */

const usd = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

const text = (value: unknown): string | null => {
  if (value === null || value === undefined) {
    return null;
  }
  const s = String(value).trim();
  return s === '' ? null : s;
};

const amountText = (value: unknown): string | null => {
  const raw = text(value);
  if (raw === null) {
    return null;
  }
  const n = Number(raw.replace(/,/g, ''));
  return Number.isFinite(n) ? usd.format(n) : raw;
};

/** The row's id as a person knows it: the invoice number, falling back to the raw id. */
export const trackingItemTitle = (item: RequestTrackingItem): string => {
  const invoiceNumber = text(item.metadata?.invoiceNumber);
  if (invoiceNumber) {
    return invoiceNumber;
  }
  const id = item.itemId ?? '';
  if (id.startsWith('dropped:')) {
    return 'Invoice not submitted';
  }
  if (id.startsWith('escalated:')) {
    return 'Email escalated';
  }
  return id || 'N/A';
};

/** An invoice row's amount, e.g. "$2,831.50"; null when unknown. */
export const trackingItemAmount = (item: RequestTrackingItem): string | null =>
  amountText(item.metadata?.amount);

/** An invoice row's vendor; null when unknown. */
export const trackingItemVendor = (item: RequestTrackingItem): string | null =>
  text(item.metadata?.vendorName);

/**
 * What happened to an invoice that did not become a new record: "Already recorded", followed by
 * the reason claims-mgmt gave when there is one. Null for any other outcome.
 */
export const trackingItemOutcome = (
  item: RequestTrackingItem
): string | null => {
  if (item.metadata?.outcome !== 'ALREADY_RECORDED') {
    return null;
  }
  const note = text(item.metadata?.note);
  return note ? `Already recorded: ${note}` : 'Already recorded';
};

/**
 * Why an item went to a person, in words. The stage is claims-mgmt's error type for an invoice
 * (`INVOICE_NUMBER_CONFLICT`, ...); one this file does not know is shown as sent.
 */
const STAGE_LABELS: Record<string, string> = {
  INVOICE_NUMBER_CONFLICT: 'Invoice number already used for a different bill',
  VENDOR_NOT_FOUND: 'Vendor not matched',
  CLAIM_NOT_ELIGIBLE: 'Claim cannot take this invoice',
  SERVER_ERROR: 'Processing error',
};

export const trackingItemStage = (item: RequestTrackingItem): string | null => {
  const stage = text(item.failureStage);
  if (stage === null) {
    return null;
  }
  return STAGE_LABELS[stage] ?? stage;
};

/**
 * The note an escalated email shows without being opened: the reason itself when one item needs a
 * person, a count when several do, and null when none does or the one escalation gave no reason.
 */
export const escalationNote = (
  tracking: RequestTrackingData | null | undefined
): string | null => {
  const escalated = (tracking?.itemTrackings ?? []).filter(
    (item) => item.status === 'ESCALATED'
  );
  if (escalated.length > 1) {
    return `${escalated.length} items need review`;
  }
  if (escalated.length === 1) {
    return text(escalated[0].failureReason);
  }
  return null;
};
